import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from '../_services/config.service';
import { MENU_HUBS, menuHubAccessibile, normMenuUrl } from '../_services/menu-hub';
import { PortalService } from '../portale/_services/portal.service';
import { UserAccessibleRoutesService } from '../dashboard/_services/user-accessible-routes.service';

/**
 * Guard di AUTORIZZAZIONE pagina: impedisce a un utente di navigare (anche via
 * deep-link diretto) a una sezione che NON è nel suo menu (catena
 * Azienda→Preset→Servizio→Pagina→Ruoli, SP `prt_sidebar_menu_get`).
 *
 * L'`AuthGuard` controlla solo l'autenticazione; questo guard aggiunge il
 * controllo di accesso alla pagina, riusando la cache `UserAccessibleRoutesService`
 * (le stesse route del menu). Se la cache non è ancora caricata (deep-link prima
 * che la sidebar carichi il menu), carica il menu on-demand prima di decidere.
 *
 * Sicurezza/robustezza:
 * - whitelist di route sempre consentite (dashboard, profilo) per evitare lock-out
 * - fail-open se il caricamento menu fallisce (errore transitorio): non blocca
 * - se il menu è caricato e la route non è accessibile → redirect a /dashboard
 */
@Injectable({ providedIn: 'root' })
export class PageAccessGuard implements CanActivateChild {
	// Route sempre consentite (non guidate dal menu): home/dashboard, profilo, ecc.
	// /workflow/execute e /workflow/detail sono azioni end-user (apertura job dal
	// widget "Lista Job" e dalle notifiche): l'utente ha job assegnati anche senza
	// avere la sezione "workflow" nel menu, quindi non vanno bloccate dalla guard.
	// /messaging e' la chat: funzione trasversale dell'utente, non una sezione di
	// menu per azienda (nessuna riga prt_page la espone, quindi senza whitelist
	// veniva respinta SEMPRE — pulsante "Apri a pagina intera" del pannello e link
	// ?conv=ID delle email di notifica finivano su /dashboard). La disponibilita'
	// e' governata da cmp_chat_mode e l'autorizzazione e' per conversazione lato
	// backend, quindi consentire la rotta non da' accesso a nulla in piu'.
	private static readonly WHITELIST = ['/', '/dashboard', '/profilo', '/profile', '/appointments', '/messaging', '/workflow/execute', '/workflow/detail'];
	// Route riservate all'utenza admin, indipendenti dall'azienda corrente
	private static readonly SOLO_ADMIN = ['/portale/onboarding-azienda'];

	constructor(
		private routesSvc: UserAccessibleRoutesService,
		private portalService: PortalService,
		private config: ConfigService,
		private router: Router,
	) {}

	canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
		const url = this.cleanUrl(state.url);

		if (this.isWhitelisted(url)) return true;

		// Creazione guidata di un'azienda: la decide SOLO l'utenza admin (lo stesso controllo del "+" in testata),
		// non il menu dell'azienda corrente — altrimenti da certe aziende il pulsante riportava in dashboard.
		if (PageAccessGuard.SOLO_ADMIN.some((p) => url === p || url.startsWith(p + '/'))) {
			if (this.config.isUserSuperAdmin()) return true;
			this.router.navigate(['/dashboard']);
			return false;
		}

		const decide = (): boolean => {
			if (this.canAccess(url)) return true;
			this.router.navigate(['/dashboard']);
			return false;
		};

		// Cache già pronta → decisione immediata
		if (this.routesSvc.isLoaded) return decide();

		// Deep-link: carico il menu on-demand, poi decido. Fail-open su errore.
		return this.portalService.getMenuItems(this.config.getPortalID(), this.config.getCompany_id()).pipe(
			map((menu: any) => {
				this.routesSvc.loadFromMenu(menu);
				return decide();
			}),
			catchError(() => of(true)), // errore caricamento menu → non bloccare
		);
	}

	/**
	 * Accesso a livello di MODULO/sezione (primo segmento), non sul path esatto:
	 * se l'utente ha una qualsiasi voce di menu sotto lo stesso modulo (es. ha
	 * /worksite/list), può accedere a tutte le sotto-rotte (/worksite/edit, /worksite/detail/5...).
	 * Necessario perché le pagine di dettaglio/creazione NON sono voci di menu.
	 */
	private canAccess(url: string): boolean {
		// match esatto/prefisso + fail-open (cache non caricata) gestiti da canAccessRoute
		if (this.routesSvc.canAccessRoute(url)) return true;
		// cruscotto di una sezione a schede (menu-hub.ts): non è una voce di menu, si apre
		// se l'utente vede almeno una delle pagine della sezione
		const hub = MENU_HUBS.find((h) => normMenuUrl(h.homeUrl) === normMenuUrl(url));
		if (hub) return menuHubAccessibile(hub, (u) => this.routesSvc.canAccessRoute(u));
		// se la cache è caricata ma vuota → utente senza permessi → nega
		if (!this.routesSvc.accessibleRoutes.length) return false;
		const mod = this.moduleRoot(url);
		return this.routesSvc.accessibleRoutes.some((r) => this.moduleRoot(r) === mod);
	}

	private moduleRoot(u: string): string {
		const seg = (u || '').split('/').filter(Boolean);
		return seg.length ? '/' + seg[0] : '/';
	}

	private cleanUrl(url: string): string {
		// rimuove query (?), matrix params (;) e frammenti
		return (url || '').split('?')[0].split(';')[0].split('#')[0];
	}

	private isWhitelisted(url: string): boolean {
		return PageAccessGuard.WHITELIST.some((w) => url === w || url.startsWith(w + '/'));
	}
}
